/**
 * @module engine/streams
 * @summary What each line on the drawing carries, and which way it runs.
 * @description
 *   This is a reading of the DOCUMENT, not of any one view. A connector's
 *   colour, its line style, its caption and the equipment at its ends are all
 *   things somebody put on the 2D drawing, and what they add up to — product or
 *   air, forwards or backwards — is a fact about the process rather than about
 *   how it happens to be drawn on a screen.
 *
 *   It lives here because three separate things now need it and none of them is
 *   the 3D view: the flow animation asks what colour a run should move in, the
 *   mass balance asks whether a run carries any product at all, and the 2D
 *   drawing asks the same question when it writes a throughput on a line.
 *   How a moving stream is DRAWN is a different question, and stays in
 *   `engine3d/flow`.
 */
import type { Connection, FlowDirection, FlowMedium, LineStyle, Pipe3, Shape } from '../types';

/** Dashed / dotted connectors are service lines, not process pipe. */
export function isServiceLine(cn: { lineStyle: LineStyle }): boolean {
  return (
    cn.lineStyle === 'dashed' ||
    cn.lineStyle === 'dashed-short' ||
    cn.lineStyle === 'dotted' ||
    cn.lineStyle === 'dash-dot'
  );
}

/** How each medium is named in the HUD. */
export const FLOW_NAMES: Record<FlowMedium, string> = {
  material: 'Material',
  air: 'Air',
  none: 'None',
};

// ─── What is in the pipe ─────────────────────────────────────────────────

/**
 * Swatch colours from the 3D paint palette that say GAS rather than product.
 * Somebody who reached for one of these has already answered the question.
 */
const GAS_HEX = new Set(['#5aa8dd', '#b9c0c8', '#d9a520']);
/** ...and the two that say product. */
const PRODUCT_HEX = new Set(['#e0682c', '#8a5a2b']);

/**
 * Equipment whose nozzles carry air whichever way the line was drawn: the
 * machines that move it, and the filters and reliefs that air alone leaves
 * through. A cyclone is deliberately NOT here — it has product coming in and
 * air going out, so its two lines have to be told apart some other way.
 */
const AIR_KIT = new Set([
  'blower',
  'rootsblower',
  'compressor',
  'vacpump',
  'airfilter',
  'dedusting',
  'airknife',
  'nitrogen',
  'prv',
]);

const AIR_WORDS = /air|vent|vacuum|purge|nitrogen|\bn2\b|exhaust|dust|blow|suction|steam/i;
const MATERIAL_WORDS = /material|product|pellet|powder|granul|resin|compound|melt|masterbatch|feed/i;

/** Everything the inference is allowed to look at, gathered from one run. */
interface Evidence {
  /** The pinned answer, when there is one. Nothing below may override it. */
  pinned?: FlowMedium;
  /** 3D paint first, then the 2D ink the run inherits. */
  colors: (string | undefined)[];
  /** Drawn as a slim lagged service line rather than as process pipe. */
  service: boolean;
  label: string;
  /** Types of the equipment at either end, where there is any. */
  ends: string[];
}

/**
 * Decide what a run carries, in order of how much the drawing actually knows.
 *
 * Every step but the last is EVIDENCE somebody left on the drawing; the last is
 * the assumption that a line on a process flow diagram carries the process
 * unless something says otherwise. That ordering is what makes the result
 * predictable: paint beats style, style beats naming, naming beats whatever the
 * line happens to be bolted to.
 */
function infer(e: Evidence): FlowMedium {
  if (e.pinned) return e.pinned;

  for (const c of e.colors) {
    const h = c?.trim().toLowerCase();
    if (!h) continue;
    if (GAS_HEX.has(h)) return 'air';
    if (PRODUCT_HEX.has(h)) return 'material';
  }
  // A slim lagged line is a utility. It may be instrument air or a signal, but
  // it is not product, and running product down it would be a lie.
  if (e.service) return 'air';

  if (AIR_WORDS.test(e.label)) return 'air';
  if (MATERIAL_WORDS.test(e.label)) return 'material';

  if (e.ends.some((t) => AIR_KIT.has(t))) return 'air';

  return 'material';
}

// ─── Which way it goes ───────────────────────────────────────────────────

/**
 * Which way a run flows: the way it was drawn, unless somebody says otherwise.
 *
 * Nothing is inferred here, on purpose. A connector already points from one
 * machine to another, and that is a statement the engineer made — guessing
 * over the top of it would mean a line quietly running backwards because of
 * what happens to be bolted to its end, with nothing on the drawing to say why.
 *
 * The exceptions are real but not decidable from a flow sheet. A blower pushes
 * on a pressure system and pulls as an exhauster; a vacuum loader sucks on one
 * nozzle and discharges by gravity out of another. Which of those lines runs
 * backwards is knowledge the engineer has and the drawing does not, so it is
 * recorded rather than deduced — Reverse in the selection bar, kept on the run,
 * saved with the file.
 */
function inferDir(pinned: FlowDirection | undefined): FlowDirection {
  return pinned ?? 'forward';
}

/** Index the shapes once, for the two entry points below. */
export function shapeIndex(shapes: Shape[]): Map<string, Shape> {
  const m = new Map<string, Shape>();
  for (const s of shapes) m.set(s.id, s);
  return m;
}

const typeAt = (byId: Map<string, Shape>, id: string | undefined): string[] => {
  const t = id ? byId.get(id)?.type : undefined;
  return t ? [t] : [];
};

/** What a 2D connector's pipe carries in the 3D view. */
export function mediumOfConnection(cn: Connection, byId: Map<string, Shape>): FlowMedium {
  return infer({
    pinned: cn.flow3,
    colors: [cn.color3, cn.color],
    service: isServiceLine(cn),
    label: cn.label || '',
    ends: [...typeAt(byId, cn.fromId), ...typeAt(byId, cn.toId)],
  });
}

/** What a pipe drawn in 3D carries. */
export function mediumOfPipe3(q: Pipe3, byId: Map<string, Shape>): FlowMedium {
  return infer({
    pinned: q.flow3,
    colors: [q.color],
    service: !!q.service,
    label: q.label || '',
    ends: [...typeAt(byId, q.from.shapeId), ...typeAt(byId, q.to.shapeId)],
  });
}

/** Which way a 2D connector's pipe flows in the 3D view. */
export function directionOfConnection(cn: Connection): FlowDirection {
  return inferDir(cn.flowDir3);
}

/** Which way a pipe drawn in 3D flows. */
export function directionOfPipe3(q: Pipe3): FlowDirection {
  return inferDir(q.flowDir3);
}
